/**
 * V109-5-2..5-5 — client for the sweep views (Reveal, Foundation Check, Compliance Pulse, the roll-up).
 *
 * Every call is workspace-scoped (`/api/w/:slug/*`): the server resolves the workspace and the caller
 * from the session. Reads: any member. Writes: starting a sweep and deciding or reopening a clue are
 * owner / admin (server-enforced; a 403 reads as plain text).
 */
import type { InfraResource, ProgramSweepReport, SweepSummary, UnresolvedClue } from '@aigovops/core';
import { req } from './api';

export type { InfraResource, ProgramSweepReport, SweepSummary, UnresolvedClue };

const w = (slug: string) => `/api/w/${encodeURIComponent(slug)}`;
const post = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const sweepKeys = {
  summary: (slug: string) => ['sweep', slug, 'summary'] as const,
  report: (slug: string, program: string) => ['sweep', slug, 'report', program] as const,
  resources: (slug: string) => ['sweep', slug, 'resources'] as const,
  clues: (slug: string) => ['sweep', slug, 'clues'] as const,
  dismissed: (slug: string) => ['sweep', slug, 'clues', 'dismissed'] as const,
  all: (slug: string) => ['sweep', slug] as const,
};

export const getSweepSummary = (slug: string) => req<{ summary: SweepSummary }>(`${w(slug)}/sweeps/summary`).then((r) => r.summary);

export const getSweepReport = (slug: string, program: string) =>
  req<{ report: ProgramSweepReport }>(`${w(slug)}/programs/${encodeURIComponent(program)}/sweep-report`).then((r) => r.report);

/** On-demand sweep (owner / admin). 409 while one runs; 429 within 15 minutes of the last; 422 with nothing to read. */
export const startSweep = (slug: string) => req<{ run: { run_id: string; state: string }; collections: number }>(`${w(slug)}/scans`, post({}));

/** BU-5: open the finding for a change that broke and has none (owner / admin; idempotent — 201 created, 200 already open). */
export const createChangeFinding = (slug: string, eventId: string) =>
  req<{ finding_id: string; created: boolean }>(`${w(slug)}/sweeps/changes/${encodeURIComponent(eventId)}/finding`, post({}));

export const listSweepResources = (slug: string) =>
  req<{ resources: InfraResource[]; can_write: boolean }>(`${w(slug)}/resources`);

export const listOpenClues = (slug: string) =>
  req<{ clues: UnresolvedClue[] }>(`${w(slug)}/resources/clues?status=open`).then((r) => r.clues);

export const resolveClue = (slug: string, clueId: string, body: { provider_key: string; product: string }) =>
  req<{ clue: UnresolvedClue }>(`${w(slug)}/resources/clues/${encodeURIComponent(clueId)}/resolve`, post(body));

/** BU-4: used, provider not in the catalog — an External service with the provider pending + a catalog request. Owner / admin. */
export const resolveClueUnlisted = (slug: string, clueId: string, body: { name: string; website?: string | null }) =>
  req<{ clue: UnresolvedClue; external_service_id: string; provider_key: string; catalog_request_id: string }>(`${w(slug)}/resources/clues/${encodeURIComponent(clueId)}/unlisted`, post(body));

export const dismissClue = (slug: string, clueId: string, reason: string) =>
  req<{ clue: UnresolvedClue }>(`${w(slug)}/resources/clues/${encodeURIComponent(clueId)}/dismiss`, post({ reason }));

/** Built-UI #6: the clues a person marked "Not ours" (the Restore row). Any member. */
export const listDismissedClues = (slug: string) =>
  req<{ clues: UnresolvedClue[] }>(`${w(slug)}/resources/clues?status=dismissed`).then((r) => r.clues);

/** Built-UI #6: Undo / Restore — a decided clue back to open (owner / admin; 409 when it is already open). */
export const reopenClue = (slug: string, clueId: string) =>
  req<{ clue: UnresolvedClue }>(`${w(slug)}/resources/clues/${encodeURIComponent(clueId)}/reopen`, post({}));
